/**
 * Contains common functions and constants which are accessible anywhere via appn DOT notation.
 * If a function is repeated 3 or more times then it should live here and then just called from the 3 places its needed.
 */
namespace appn.ui {

    /**
     * Contextual colors when you need them
     */
    export const colorPalette: utils.ColorPalette = new utils.ColorPalette();

    export function getMainContentForm() {

        const appnForm = $('form#appnForm');
        if (utils.hasValue(appnForm))
            return appnForm; // appnForm is mostly always the main content form

        // Otherwise fall back to the first form in the content area
        return $('section.content form:first');
    }

    export function handleParentResize(timeOut: number = 0, addHeightValue: number = 0) {
        var $parentiFrame = parent.document.getElementById('e-modal-iframe');

        setTimeout(function() {
            if ($parentiFrame === null)
                return;
            $parentiFrame.style.height = 'auto';
            $parentiFrame.style.height = document.documentElement.scrollHeight + addHeightValue + 'px';
        }, timeOut);
    }

    /**
     * Returns if we are in designers namespace or not
     */
    export function designerAreaCheck(): { isDesigner: boolean, type: appn.ScreenType | undefined, isSync?: boolean; } {
        const designerMain = (window as any).appn.designer?.main;
        if (designerMain) {
            return { isDesigner: true, type: designerMain.screenType };
        }

        const syncDesigner = (window as any).sync?.syncs?.SyncDesigner;
        if (syncDesigner) {
            return { isDesigner: true, type: undefined, isSync: true };
        }

        return { isDesigner: false, type: undefined };
    }

    // https://adminlte.io/docs/3.0/javascript/card-widget.html
    export function cardExpand(card: JQuery<HTMLElement>) {
        if (!utils.hasValue(card))
            return;

        (card as any).CardWidget('expand');
    }

    // https://adminlte.io/docs/3.0/javascript/card-widget.html
    export function cardCollapse(card: JQuery<HTMLElement>) {
        if (!utils.hasValue(card))
            return;

        (card as any).CardWidget('collapse');
    }

    // https://adminlte.io/docs/3.0/javascript/card-widget.html
    export function cardToggle(card: JQuery<HTMLElement>) {
        if (!utils.hasValue(card))
            return;

        (card as any).CardWidget('toggle');
    }

    export function tryResetFiltersEvent() {
        // Hook up reset filters link
        $('.link-reset-filters').on('click', function(e) {
            e.preventDefault();
            $('.btn-resetFilters').click();
        });
    }

    /**
     * Apply number abbreviation styling to number fields containing class: 
     * appn-num-abbr.
     */
    export function abbreviateNumbers() {
        $('.appn-num-abbr').each(function(index, numberElement) {
            const suffixes = [appn.emptyString, 'k', 'm', 'b', 't'];
            const innerText: any = numberElement.innerText;

            if (isNaN(innerText))
                return;

            const originalNumber: number = innerText;

            if (originalNumber < 10000)
                return;

            let result = originalNumber;

            let suffixNum = Math.floor(String(originalNumber).length / 3);

            if (suffixNum !== 0) {
                result = originalNumber / Math.pow(1000, suffixNum);

                if (String(result).contains('.') && String(result).substr(0, String(result).indexOf('.')) === '0') {
                    result = Math.round((result * 1000));
                    suffixNum--;
                }
            }

            if (result % 1 !== 0)
                result = Number(result.toFixed(1));

            numberElement.setAttribute('title', String(originalNumber));
            numberElement.innerText = result + suffixes[suffixNum];
        });
    }

    /**
     * Opens a new browser window from a link
     * @param mylink
     * @param windowname
     */
    export function popup(mylink: any): boolean {
        if (!window.focus) return true;

        let href;
        if (typeof mylink === 'string')
            href = mylink;
        else if (mylink.href)
            href = mylink.href;
        else
            href = $(mylink).data('src');

        window.open(href, 'popup', 'width=960,height=600,resizable=yes,scrollbars=yes');
        return false;
    }

    /**
     * Opens an image in a new window for easy download
     * Just add open-onclick class to element
     */
    export function initOpenOnClick() {
        $('.open-onclick, .fileinput .fileinput-new.thumbnail img').off().on('click', function(e) {
            e.preventDefault();
            e.stopPropagation();
            appn.dialog.viewMediaFromElement($(this));
        });
    }

    /**
     * Toggle elements enabled and disabled state
     * @param $elementToToggle
     */
    export function toggleEnabledOrDisabled($elementToToggle: JQuery<HTMLElement>) {
        if (isElementDisabled($elementToToggle))
            enableElement($elementToToggle);
        else
            disableElement($elementToToggle);
    }

    /**
     * Check if an element is disabled.
     * Caters for class=disabled and/or attr=disabled
     * @param $elementToCheck
     */
    export function isElementDisabled($elementToCheck: JQuery<HTMLElement>): boolean {
        return $elementToCheck.is('[disabled]') || $elementToCheck.hasClass('disabled');
    }

    /**
     * Enables element(s)
     * Caters for class=disabled and/or attr=disabled
     * @param $elementToEnable
     */
    export function enableElement($elementToEnable: JQuery<HTMLElement>): JQuery<HTMLElement> {
        $elementToEnable.removeClass('disabled').removeAttr('disabled');
        return $elementToEnable;
    }

    /**
     * Disables element(s)
     * Caters for class=disabled and/or attr=disabled
     * @param $elementToDisable
     */
    export function disableElement($elementToDisable: JQuery<HTMLElement>): JQuery<HTMLElement> {
        $elementToDisable.addClass('disabled').attr('disabled', 'disabled');
        return $elementToDisable;
    }

    /**
     * Enables the $elementToToggle if the $elementToWatch has a value
     * Disables the $elementToToggle if the $elementToWatch does NOT have a value
     * @param $elementToWatch
     * @param $elementToToggle
     */
    export function enableDisableIfPopulated($elementToWatch: JQuery<HTMLInputElement>, $elementToToggle: JQuery<HTMLElement>, invert?: boolean) {
        // Enable disable element based on if it has a value or not
        $elementToWatch.on('input', function(e) {
            if (($elementToWatch.val() as string).length > 0) {
                if (invert)
                    disableElement($elementToToggle);
                else
                    enableElement($elementToToggle);

                return;
            }

            if (invert)
                enableElement($elementToToggle);
            else
                disableElement($elementToToggle);
        });

        // Disable the element if there is val
        if (($elementToWatch.val() as string).length < 1)
            if (invert)
                enableElement($elementToToggle);
            else
                disableElement($elementToToggle);
    }

    /**
     * Shows an export confirmation notification
     * @param entityName
     */
    export function showExportConfirmation(entityName: string) {
        entityName = T('{0} Export', entityName ? entityName : '').trim();
        const title = T('<b>{0} scheduled</b>', entityName).toUpperCase();
        appn.toast.notify(toast.Type.info, T(`{0}#TITLE#Check your <a class='cursor-pointer' data-notifications-launch='{1}'>notifications</a> for progress and downloads.`, title, appn.urls.notifications.index));
    }

    /**
     * Shows an import confirmation notification
     * @param entityName
     */
    export function showImportConfirmation(entityName: string) {
        entityName = T('{0} Import', entityName ? entityName : '').trim();
        const title = T('<b>{0} scheduled</b>', entityName).toUpperCase();
        appn.toast.notify(toast.Type.info, T(`{0}#TITLE#Check your <a class='cursor-pointer' data-notifications-launch='{1}'>notifications</a> for progress.`, title, appn.urls.notifications.index));
    }

    export function initDateAndTimePickers() {
        // Initialize all the pickers
        datePicker($('.datepicker'));
        timePicker($('.timepicker'));
        dateTimePicker($('.datetimepicker'));

        // Add Picker-Related events
        addDTEvents();
    }

    function addDTEvents(): void {
        // Date Picker
        $(document).on('dp.show dp.hide', '.datepicker', function() {
            resizeAndScrollTo($(this));
        });

        // Time Picker
        $(document).on('dp.show dp.hide', '.timepicker', function() {
            resizeAndScrollTo($(this));
        });

        // DateTime Picker
        $(document).on('dp.show dp.hide', '.datetimepicker', function() {
            resizeAndScrollTo($(this));
        });
    }

    function resizeAndScrollTo(inputElement: JQuery<HTMLElement>) {
        handleParentResize(0, 15);
        setTimeout(function() {
            inputElement[0].scrollIntoView({
                behavior: "auto",
                block: "center"
            });
        }, 5);
    }

    /**
     * Inits a Date ONLY picker for the given selector
     * @param $selector
     */
    export function datePicker($selector: JQuery<HTMLElement>, afterChangedCallback?: Function, format?: string) {

        createAndPositionDatePicker($selector, format ? format : 'DD-MMM-YYYY', afterChangedCallback); // refer: http://momentjs.com/docs/#/displaying/format/
    }

    /**
     * Inits a Time ONLY picker for the given selector
     * @param $selector
     */
    export function timePicker($selector: JQuery<HTMLElement>, afterChangedCallback?: Function) {
        createAndPositionDatePicker($selector, 'HH:mm', afterChangedCallback);
    }

    /**
     * Inits a Date AND Time picker for the given selector
     * @param $selector
     */
    export function dateTimePicker($selector: JQuery<HTMLElement>, afterChangedCallback?: Function, format?: string) {
        createAndPositionDatePicker($selector, format ?? 'DD-MMM-YYYY HH:mm', afterChangedCallback);
    }

    /**
     * THIS IS A HACK FOR A PREVIOUS VERSION AND NEEDS TO BE RELOOKED AT AND FIXED
     * Creates the datetimepicker and fixes positioning issues.
     * Positioning is broken when used within a scrollable div.
     * Solution is to attach the dp to <body> and then realign during show().
     * Credit and discussion on issue: https://github.com/Eonasdan/bootstrap-datetimepicker/issues/790#issuecomment-234652413
     * @param $selector
     * @param inputFormat
     */
    function createAndPositionDatePicker($selector: JQuery<HTMLElement>, inputFormat: string, afterChangedCallback?: Function): void {
        const $dp = ($selector as any).datetimepicker({
            allowInputToggle: true,
            locale: 'en',
            format: inputFormat,
            sideBySide: true,
            keyBinds: { t: null } // remove irritating 't' default bind which breaks MMM input (e.g. 'Oct')
        });

        $dp.on('dp.show', function() {
            const datepicker = $('body').find('.bootstrap-datetimepicker-widget:last'),
                position = datepicker.offset(),
                parent = datepicker.parent(),
                parentPos = parent.offset(),
                width = datepicker.width(),
                parentWid = parent.width();

            // move datepicker to the exact same place it was but attached to body
            datepicker.appendTo('body');
            datepicker.css({
                position: 'absolute',
                top: position ? position.top : 0,
                bottom: 'auto',
                left: position ? position.left : 0,
                right: 'auto'
            });

            // if datepicker is wider than the thing it is attached to then move it so the centers line up
            if (parentPos!.left + parentWid! < position!.left + width!) {
                let newLeft = parentPos!.left;
                newLeft += parentWid! / 2;
                newLeft -= width! / 2;
                datepicker.css({ left: newLeft });
            }

            if ($selector.attr('class')?.containsIgnoreCase('datetime')) {
                datepicker.addClass('loaded');
            }
        });

        $dp.on('dp.change', function() {
            if (afterChangedCallback)
                afterChangedCallback();
        });
    }

    /**
     * Changes image colors via hidden HTML5 canvas
     * Modified from http://codereview.stackexchange.com/q/45041
     * NOTE: The <img> passed in need to have its src loaded.
     *       You should use .on('load', function()) to be notified when the image is loaded before applying color changes
     */
    export function changeIconColor(iconElement: HTMLImageElement, color: string, imgWidth: number, imgHeight: number, alpha: number): string {
        const canvas = document.createElement('canvas'), // shared instance
            context = canvas.getContext('2d');

        const desaturate = function() {
            const imageData = context!.getImageData(0, 0, canvas.width, canvas.height);
            const pixels = imageData.data;
            let i, l, r, g, b, a, average;

            for (i = 0, l = pixels.length; i < l; i += 4) {
                a = pixels[i + 3];
                if (a === 0)
                    continue; // skip if pixel is transparent

                r = pixels[i];
                g = pixels[i + 1];
                b = pixels[i + 2];

                average = (r + g + b) / 3 >>> 0; // quick floor
                pixels[i] = pixels[i + 1] = pixels[i + 2] = average;
            }

            context!.putImageData(imageData, 0, 0);
        };

        const colorize = function(color: any, alpha: any) {
            context!.globalCompositeOperation = 'source-atop';
            context!.globalAlpha = alpha;
            context!.fillStyle = color;
            context!.fillRect(0, 0, canvas.width, canvas.height);
            // reset
            context!.globalCompositeOperation = 'source-over';
            context!.globalAlpha = 1.0;
        };

        if (color === 'transparent' || color === appn.emptyString)
            return iconElement.src;

        canvas.width = imgWidth;
        canvas.height = imgHeight;
        context!.clearRect(0, 0, canvas.width, canvas.height);
        context!.drawImage(iconElement, 0, 0, canvas.width, canvas.height);
        desaturate();
        colorize(color, alpha);
        return canvas.toDataURL('image/png', 1);
    }

    /**
     * Creates a new third party map and injects it into the mapName element.
     * @param mapname
     * @param centerLat
     * @param centerLon
     * @param zoomLevel
     * @param hideZoomControl
     */
    export function mapInit(mapname: string, centerLat: number, centerLon: number, zoomLevel: number, hideZoomControl: boolean): any {
        const mapDiv = $('#' + mapname);
        if (!mapDiv.length)
            return; // Bail if there is no #map div

        const map = new L.Map(mapname, {
            center: new L.LatLng(centerLat, centerLon),
            minZoom: 2,
            zoom: zoomLevel,
            zoomControl: false // add at desired position after creation
        });
        (map as any).attributionControl.setPrefix(appn.emptyString);

        // place zoom control in top right
        if (!hideZoomControl)
            L.control.zoom({ position: 'topright' }).addTo(map);

        // create a new tile layer
        const tileUrl = 'https://{s}.tile.thunderforest.com/appn-h2w/{z}/{x}/{y}{r}.png?apikey=9620569baf5e41caa7213c5e4812f729';
        const layer = new L.TileLayer(tileUrl, {
            attribution: '<a href="http://www.thunderforest.com">Thunderforest</a>, <a href="http://www.leafletjs.com">Leaflet</a>, <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>',
            maxZoom: 18
        });

        // add the layer to the map
        map.addLayer(layer);

        // rego for listeners
        layer.on("loading", function() { appn.utils.mapLoadState = "loading"; });
        layer.on("load", function() { appn.utils.mapLoadState = "loaded" });

        return map;
    }

    /**
     * Checks if the server responded with an login form.
     * This means that there was a logout on the server and that the session is invalid.
     */
    export function validateSessionFromAjax(jqxhrResponse: JQueryXHR) {
        if (jqxhrResponse && jqxhrResponse.responseText)
            if (jqxhrResponse.responseText.indexOf('form-signin') !== -1) {

                if ($('.bootbox').hasClass('in') && $('.bootbox').hasClass('signinrequired'))
                    return; // multiple ajax could come through this prevents multiple sign in dialogs from showing at once

                // Check if the error is because of a bad session of which the result is a redirect to logon from teh server.
                appn.dialog.alert({
                    modalColor: 'modal-info',
                    title: T('Sign In Required'),
                    className: 'signinrequired',
                    message: T('Your session has been invalidated.<br>This may have occurred because you logged into a different account in another browser tab.<br />Please Sign In to resume using the platform.'),
                    buttons: {
                        ok: {
                            label: '<span>' + T('OK') + '</span>',
                            className: 'btn-success'
                        }
                    },
                    callback: function (result: any) {
                        appn.location.reloadCurrentPage();
                    }
                });
            }
    }

    export function spinnerInject(appenedToElement: JQuery<HTMLElement>) {
        appenedToElement.append(getSpinner());
    }

    export function spinnerRemove(removeFromElement: JQuery<HTMLElement>) {
        removeFromElement.find('.appn-spinner').remove();
    }

    export function getSpinner(classes?: string): string {
        return `<div class="appn-spinner spinner-border text-primary ${(classes ? classes : emptyString)}" role="status"></div>`;
    }

    /**
     * Used in some cases where icheck value check doesnt work.
     * This will force the value to reflect on the hidden input
     */
    export function checkboxForceOnChanged(selector: string) {
        $(selector).off().on('change', function(this: HTMLElement, e: Event) {
            const isChecked = $(this).is(':checked');
            const hiddenInput = $(this).closest('.appn-checkbox').find('input[type="hidden"]');
            if (isChecked)
                hiddenInput.prop('value', true);
            else
                hiddenInput.prop('value', false);
        });

        $(selector).each(function(index: number, element) {
            const hiddenInput = $(element).closest('.appn-checkbox').find('input[type="hidden"]');
            const isValueChecked = hiddenInput.prop('value') === 'true' ? true : false;
            if (isValueChecked)
                $(element).closest('.appn-checkbox').find('label').click();
        });
    }

    export function showTimeZoneDialog() {
        appn.dialog.custom({
            message: '<div class="form-group">' + appn.elementBuilder.buildSelect(appn.timeZones, 'timeZonesSelect', '', T('List of Time Zones')) + `<br /><label class="form-control-sm col-form-label" for="timeZoneValue">${T('Time Zone Value:')}</label><input type="text" id="timeZoneValue" class="form-control-sm form-control" name="timeZoneValue" readonly></div>`,
            title: T('List of Time Zones'),
            modalColor: 'modal-info',
            // Might need to add buttons
            closeButton: true
        });
        $('#timeZonesSelect').off().on('change', function() {
            $('#timeZoneValue').val($('#timeZonesSelect').val() ?? appn.emptyString);
        });
    }

    export function initCardSummaries() {
        if ($('.summary-field').length <= 0)
            return;

        $('.card.collapsed-card:has(.summary-field)').each(function() {
            let $card = $(this);
            let $summaryContainer = buildCardSummary($card);
            $summaryContainer.show();
        });
        
        // listens to custom AdminLTE events to avoid race conditions.
        // might need updating when we upgrade AdminLTE versions.
        $('.card:has(.card-body .summary-field)')
            .on('expanded.lte.cardwidget', function() {
                let $card = $(this);
                $card.find('.card-summary').slideUp(300);
            })
            .on('collapsed.lte.cardwidget', function() {
                let $card = $(this);
                let $summaryContainer = buildCardSummary($card);
                $summaryContainer.slideDown(300);
            });
    }
    
    function buildCardSummary($card: JQuery<HTMLElement>): JQuery<HTMLElement>
    {
        let $header = $card.find('.card-header');

        // Inject summary container if not already there
        if ($header.find('.card-summary').length === 0) {
            $header.addClass('flex-wrap');

            const $summary = $('<div class="card-summary w-100 mt-2" style="display: none;"></div>');
            $summary.on('click', function() {
                $(this).closest('.card')
                    .find('[data-card-widget="collapse"]')
                    .trigger('click');
            });
            $header.append($summary);
        }

        let $summaryContainer = $card.find('.card-summary');
        $summaryContainer.empty();

        // Loop through each summary row and build the HTML
        $card.find('.summary-field').each(function() {
            let $summaryRow = $('<div class="form-group row mb-0"></div>');

            $(this).find('label.col-form-label').each(function() {
                let $originalLabel = $(this);
                let labelClasses = $originalLabel.attr('class');
                let cleanLabelText = $originalLabel.text().trim();

                let inputId = $originalLabel.attr('for');
                let $input = $('#' + inputId);

                if ($input.length > 0) {
                    let wrapperClasses = $input.closest('[class*="col-"]').attr('class') || 'col-md-9';
                    let value: string | number | string[]  = '';

                    if ($input.is('select')) {
                        value = $input.find('option:selected').text();
                    } else {
                        value = $input.val() || '-';
                    }

                    $summaryRow.append(`
                        <label class="${labelClasses} mb-0">${cleanLabelText}</label>
                        <div class="${wrapperClasses}">
                            <div class="form-control-plaintext font-weight-bold text-dark mb-0">${value}</div>
                        </div>
                    `);
                }
            });
            $summaryContainer.append($summaryRow);
        });

        return $summaryContainer;
    }

    export interface CountryStateOptions {
        countrySelector: string;
        stateTextSelector: string;
        stateSelectSelector: string;
        countryStates: Record<string, Record<string, string>>;
    }

    /**
     * Initializes a dynamic toggle between a state text input and a dropdown.
     */
    export function initCountryStateDropdown(options: CountryStateOptions) {
        const $country = $(options.countrySelector);
        const $stateText = $(options.stateTextSelector);
        const $stateSelect = $(options.stateSelectSelector);

        if (!$country.length || !$stateText.length || !$stateSelect.length || !options.countryStates) return;

        // Ensure the hidden text field is validated even when hidden. 
        // Global ignore rule in appn.ts handles fields with this class.
        $stateText.addClass('validate-always');

        function updateStateField(isInit: boolean = false) {
            const countryCode = ($country.val() as string || '').toUpperCase();
            const states = options.countryStates[countryCode];

            appn.select.destroy(options.stateSelectSelector);

            if (states) {
                $stateText.hideElement();
                $stateSelect.removeAttr('name').empty().append($('<option>', { value: '', text: T('Choose...') }));

                $.each(states, (code, name) => $stateSelect.append($('<option>', { value: code, text: name })));

                if (isInit) {
                    const currentState = $stateText.val() as string;
                    if (currentState) {
                        const code = states[currentState] ? currentState : Object.keys(states).find(k => k.toLowerCase() === currentState.toLowerCase() || states[k].toLowerCase() === currentState.toLowerCase());
                        if (code) $stateSelect.val(code);
                    }
                } else {
                    $stateText.val('');
                    $stateSelect.val('');
                }

                $stateSelect.removeClass('d-none').removeAttr('data-oldclasses');
                appn.select.init({ selector: options.stateSelectSelector });
                $stateSelect.showElement().next('.select2').showElement();
            } 
            else {
                $stateSelect.hideElement().next('.select2').hideElement();
                $stateText.showElement();
                if (!isInit) $stateText.val('');
            }
            syncValidationStyling();
        }

        function syncValidationStyling() {
            const hasError = $stateText.hasClass('input-validation-error');
            
            // Use native .is-invalid so Select2 Bootstrap theme applies red border automatically
            $stateSelect.toggleClass('is-invalid', hasError);

            const $select2 = $stateSelect.next('.select2');
            if ($select2.length) {
                // Remove error class from span so validator doesn't hide it
                $select2.removeClass('input-validation-error');
            }
        }

        $country.on('change', () => updateStateField());
        
        $stateSelect.on('change', () => {
            if ($stateSelect.is(':visible')) {
                $stateText.val($stateSelect.val() as string);
                if (($stateText as any).valid) ($stateText as any).valid();
            }
            syncValidationStyling();
        });

        // Mirror validation class changes from text input to Select2
        const observer = new MutationObserver(() => syncValidationStyling());
        observer.observe($stateText[0], { attributes: true, attributeFilter: ['class'] });

        updateStateField(true);
    }

    /**
     * Initializes tri-state checkboxes. 
     * Cycles: null (indeterminate) → true (checked) → false (unchecked) → null
     */
    export function initTriStateCheckboxes(container?: HTMLElement): void {
        const $wrappers = $(container || document).find('.appn-checkbox.tristate');
        
        $wrappers.each(function() {
            const $wrapper = $(this);
            const $checkbox = $wrapper.find('input[type="checkbox"]');
            const $hidden = $wrapper.find('input[type="hidden"]');
            const $label = $wrapper.find('label');
            
            if (!$checkbox.length || !$hidden.length || !$label.length)
                return;
            
            // Set initial visual state based on hidden value
            const hiddenVal = $hidden.val() as string;
            if (hiddenVal === '')
                ($checkbox[0] as HTMLInputElement).indeterminate = true;
            
            // Attach click handler on label (icheck CSS makes label clickable)
            $label.off('click').on('click', function(e) {
                e.preventDefault();
                e.stopPropagation();
                
                const checkbox = $checkbox[0] as HTMLInputElement;
                const hidden = $hidden[0] as HTMLInputElement;
                
                if (checkbox.indeterminate) {
                    // null → true
                    checkbox.indeterminate = false;
                    checkbox.checked = true;
                    hidden.value = 'true';
                }
                else if (checkbox.checked) {
                    // true → false
                    checkbox.indeterminate = false;
                    checkbox.checked = false;
                    hidden.value = 'false';
                }
                else {
                    // false → null
                    checkbox.indeterminate = true;
                    checkbox.checked = false;
                    hidden.value = '';
                }
            });
        });
    }
}
